import { useState, useEffect, useMemo } from 'react';
import { Link, useSearchParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import Navbar from '../components/Navbar';
import { Button } from '../components/ui/button';
import { Headphones, ChevronRight, BookOpen, Search, X, Star, Download, Library, Globe, Loader2 } from 'lucide-react';
import { Checkbox } from '../components/ui/checkbox';
import { Input } from '../components/ui/input';
import { useCategories } from '../hooks/useCategories';
import { API } from '../App';
import { useOwnedContent } from '../hooks/useOwnedContent';
import { BoughtBadge } from '../components/BoughtBadge';

const SUBJECT_STYLES = [
  { name: 'All', bg: 'bg-slate-100', hover: 'hover:bg-slate-200' },
  { name: 'Physics', bg: 'bg-blue-100', hover: 'hover:bg-blue-200' },
  { name: 'Chemistry', bg: 'bg-green-100', hover: 'hover:bg-green-200' },
  { name: 'Biology', bg: 'bg-teal-100', hover: 'hover:bg-teal-200' },
  { name: 'Mathematics', bg: 'bg-yellow-100', hover: 'hover:bg-yellow-200' },
  { name: 'History', bg: 'bg-purple-100', hover: 'hover:bg-purple-200' },
  { name: 'English', bg: 'bg-pink-100', hover: 'hover:bg-pink-200' },
  { name: 'Geography', bg: 'bg-orange-100', hover: 'hover:bg-orange-200' },
  { name: 'Kiswahili', bg: 'bg-red-100', hover: 'hover:bg-red-200' },
  { name: 'Economics', bg: 'bg-cyan-100', hover: 'hover:bg-cyan-200' },
  { name: 'Science', bg: 'bg-lime-100', hover: 'hover:bg-lime-200' },
];

const SOURCE_LABELS = {
  openstax: { name: 'OpenStax', color: 'bg-orange-500' },
  openlibrary: { name: 'Open Library', color: 'bg-blue-500' },
  gutenberg: { name: 'Gutenberg', color: 'bg-emerald-500' },
};

// Subject-based gradient covers for library books
const SUBJECT_COVERS = {
  Mathematics: { from: '#f59e0b', to: '#d97706', icon: 'M', accent: '#fbbf24' },
  Physics: { from: '#3b82f6', to: '#1d4ed8', icon: 'P', accent: '#60a5fa' },
  Chemistry: { from: '#10b981', to: '#059669', icon: 'C', accent: '#34d399' },
  Biology: { from: '#14b8a6', to: '#0d9488', icon: 'B', accent: '#5eead4' },
  History: { from: '#8b5cf6', to: '#6d28d9', icon: 'H', accent: '#a78bfa' },
  English: { from: '#ec4899', to: '#be185d', icon: 'E', accent: '#f472b6' },
  Geography: { from: '#f97316', to: '#c2410c', icon: 'G', accent: '#fb923c' },
  Economics: { from: '#06b6d4', to: '#0e7490', icon: '$', accent: '#22d3ee' },
  Science: { from: '#84cc16', to: '#4d7c0f', icon: 'S', accent: '#a3e635' },
  Kiswahili: { from: '#ef4444', to: '#b91c1c', icon: 'K', accent: '#f87171' },
  General: { from: '#6366f1', to: '#4338ca', icon: 'L', accent: '#818cf8' },
};

function AllEbooks() {
  const { levels: LEVELS } = useCategories();
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const isBought = useOwnedContent();
  const [ebooks, setEbooks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedLevel, setSelectedLevel] = useState('');
  const [selectedSubject, setSelectedSubject] = useState('All');
  const [showOnlyFree, setShowOnlyFree] = useState(false);
  const [showOnlyAudio, setShowOnlyAudio] = useState(false);
  const [sortBy, setSortBy] = useState('newest');

  // Public Library state
  const [activeSection, setActiveSection] = useState(searchParams.get('tab') === 'public-library' ? 'public-library' : 'darasani');
  const [libraryBooks, setLibraryBooks] = useState([]);
  const [libraryLoading, setLibraryLoading] = useState(false);
  const [librarySearch, setLibrarySearch] = useState('');
  const [librarySubject, setLibrarySubject] = useState('All');
  const [libraryLevel, setLibraryLevel] = useState('');

  useEffect(() => {
    axios.get(`${API}/ebooks?limit=100`)
      .then(res => { setEbooks(res.data); setLoading(false); })
      .catch(() => setLoading(false));
  }, []);

  // Load public library books when tab is activated
  useEffect(() => {
    if (activeSection === 'public-library' && libraryBooks.length === 0) {
      loadLibraryBooks();
    }
  }, [activeSection]);

  const loadLibraryBooks = async (params = {}) => {
    setLibraryLoading(true);
    try {
      const res = await axios.get(`${API}/library/books`, { params: { limit: 100, ...params } });
      setLibraryBooks(res.data);
    } catch (err) {
      console.error('Failed to load public library:', err);
    }
    setLibraryLoading(false);
  };

  const searchLibrary = async () => {
    const params = {};
    if (librarySearch.trim()) params.q = librarySearch.trim();
    if (librarySubject !== 'All') params.subject = librarySubject;
    if (libraryLevel) params.level = libraryLevel;
    loadLibraryBooks(params);
  };

  // Filtered library books (client-side for quick subject/level filtering of loaded data)
  const filteredLibrary = useMemo(() => {
    let result = [...libraryBooks];
    if (librarySubject !== 'All') {
      result = result.filter(b => b.subject?.toLowerCase() === librarySubject.toLowerCase());
    }
    if (libraryLevel) {
      result = result.filter(b => b.level?.toLowerCase() === libraryLevel.toLowerCase());
    }
    return result;
  }, [libraryBooks, librarySubject, libraryLevel]);

  // Filter and sort Darasani ebooks
  const filteredEbooks = useMemo(() => {
    let result = [...ebooks];
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      result = result.filter(ebook =>
        ebook.title?.toLowerCase().includes(query) ||
        ebook.author?.toLowerCase().includes(query) ||
        ebook.description?.toLowerCase().includes(query)
      );
    }
    if (selectedSubject !== 'All') {
      result = result.filter(ebook =>
        ebook.subject === selectedSubject ||
        ebook.title?.toLowerCase().includes(selectedSubject.toLowerCase()) ||
        ebook.category?.toLowerCase().includes(selectedSubject.toLowerCase())
      );
    }
    if (selectedLevel) result = result.filter(ebook => ebook.level === selectedLevel);
    if (showOnlyFree) result = result.filter(ebook => ebook.price === 0);
    if (showOnlyAudio) result = result.filter(ebook => ebook.audio_enabled || ebook.has_audio);
    switch (sortBy) {
      case 'newest': result.sort((a, b) => new Date(b.created_at || 0) - new Date(a.created_at || 0)); break;
      case 'popular': result.sort((a, b) => (b.downloads || 0) - (a.downloads || 0)); break;
      case 'rating': result.sort((a, b) => (b.rating || 0) - (a.rating || 0)); break;
      case 'price-low': result.sort((a, b) => (a.price || 0) - (b.price || 0)); break;
      case 'price-high': result.sort((a, b) => (b.price || 0) - (a.price || 0)); break;
      default: break;
    }
    return result;
  }, [ebooks, searchQuery, selectedLevel, selectedSubject, showOnlyFree, showOnlyAudio, sortBy]);

  const clearFilters = () => {
    setSearchQuery(''); setSelectedLevel(''); setSelectedSubject('All');
    setShowOnlyFree(false); setShowOnlyAudio(false); setSortBy('newest');
  };

  const activeFiltersCount = [selectedSubject !== 'All' ? selectedSubject : '', selectedLevel, showOnlyFree ? 'free' : '', showOnlyAudio ? 'audio' : ''].filter(Boolean).length;

  // Helper to determine where a library book card should link
  const getLibraryBookLink = (book) => {
    // Curated books (openstax) open in our in-app reader
    if (book.source === 'openstax') return `/public-library/${book.id}/read`;
    // External books open externally as fallback
    return book.read_url || '#';
  };

  const isInternalBook = (book) => book.source === 'openstax';

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <div className="animate-spin rounded-full h-12 w-12 border-4 border-teal-500 border-t-transparent"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50" data-testid="all-ebooks-page">
      <Navbar />

      {/* Hero Header */}
      <div className="bg-gradient-to-br from-teal-500 via-emerald-500 to-green-500 text-white">
        <div className="max-w-7xl mx-auto px-4 md:px-8 lg:px-12 py-10">
          <div className="flex items-center gap-2 text-sm text-white/70 mb-4">
            <Link to="/" className="hover:text-white">Home</Link>
            <ChevronRight className="w-4 h-4" />
            <span className="text-white">eBooks Library</span>
          </div>

          <h1 className="text-3xl md:text-4xl font-black mb-2">eBooks & Study Materials</h1>
          <p className="text-white/80 mb-6">
            {activeSection === 'darasani'
              ? `Discover ${ebooks.length}+ books for your learning journey`
              : `${filteredLibrary.length}+ free textbooks — read directly in the app`}
          </p>

          {/* Section Toggle */}
          <div className="flex gap-3 mb-6" data-testid="ebook-section-toggle">
            <button
              onClick={() => { setActiveSection('darasani'); setSearchParams({}); }}
              className={`px-6 py-3.5 rounded-2xl font-bold text-base transition-all ${
                activeSection === 'darasani'
                  ? 'bg-white text-teal-700 shadow-xl shadow-white/20 scale-105'
                  : 'bg-amber-500 text-white hover:bg-amber-400 shadow-lg shadow-amber-500/30'
              }`}
              data-testid="darasani-ebooks-tab"
            >
              <BookOpen className="w-5 h-5 inline mr-2" />Darasani eBooks
            </button>
            <button
              onClick={() => { setActiveSection('public-library'); setSearchParams({ tab: 'public-library' }); }}
              className={`px-6 py-3.5 rounded-2xl font-bold text-base transition-all ${
                activeSection === 'public-library'
                  ? 'bg-white text-teal-700 shadow-xl shadow-white/20 scale-105'
                  : 'bg-indigo-500 text-white hover:bg-indigo-400 shadow-lg shadow-indigo-500/30'
              }`}
              data-testid="public-library-tab"
            >
              <Library className="w-5 h-5 inline mr-2" />Public Library
            </button>
          </div>

          {/* Search Bar */}
          {activeSection === 'darasani' ? (
            <div className="relative max-w-2xl">
              <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 w-5 h-5 text-slate-400" />
              <Input
                type="text"
                placeholder="Search by title, author..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full pl-12 pr-4 py-4 text-slate-900 bg-white rounded-xl border-0 shadow-lg focus:ring-2 focus:ring-white/50"
                data-testid="ebook-search-input"
              />
              {searchQuery && (
                <button onClick={() => setSearchQuery('')} className="absolute right-4 top-1/2 transform -translate-y-1/2">
                  <X className="w-5 h-5 text-slate-400 hover:text-slate-600" />
                </button>
              )}
            </div>
          ) : (
            <div className="flex gap-2 max-w-3xl">
              <div className="relative flex-1">
                <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 w-5 h-5 text-slate-400" />
                <Input
                  type="text"
                  placeholder="Search textbooks (biology, algebra, history...)"
                  value={librarySearch}
                  onChange={(e) => setLibrarySearch(e.target.value)}
                  onKeyDown={(e) => e.key === 'Enter' && searchLibrary()}
                  className="w-full pl-12 pr-4 py-4 text-slate-900 bg-white rounded-xl border-0 shadow-lg focus:ring-2 focus:ring-white/50"
                  data-testid="library-search-input"
                />
              </div>
              <Button onClick={searchLibrary} className="bg-white text-teal-700 hover:bg-white/90 px-6 py-4 rounded-xl font-semibold shadow-lg" data-testid="library-search-btn">
                Search
              </Button>
            </div>
          )}
        </div>
      </div>

      {/* ============ DARASANI eBOOKS ============ */}
      {activeSection === 'darasani' && (
        <>
          <div className="bg-white/95 backdrop-blur-md border-b border-slate-200 shadow-md sticky top-16 z-40" data-testid="ebook-filter-sticky-bar">
            <div className="max-w-7xl mx-auto px-4 md:px-8 lg:px-12 py-3">
              <div className="flex items-center gap-3">
                <div className="relative shrink-0" data-testid="ebook-level-filter">
                  <select value={selectedLevel} onChange={(e) => setSelectedLevel(e.target.value)}
                    className="appearance-none bg-indigo-600 text-white pl-3 pr-8 py-2 rounded-full font-semibold text-sm cursor-pointer shadow-lg shadow-indigo-600/20 focus:outline-none focus:ring-2 focus:ring-indigo-400"
                    data-testid="ebook-level-select">
                    <option value="" className="text-slate-900 bg-white">All Levels</option>
                    {LEVELS.map(level => <option key={level} value={level} className="text-slate-900 bg-white">{level}</option>)}
                  </select>
                  <ChevronRight className="absolute right-2.5 top-1/2 -translate-y-1/2 w-4 h-4 text-white pointer-events-none rotate-90" />
                </div>
                <div className="flex gap-2 filter-pills-row flex-1 min-w-0">
                  {SUBJECT_STYLES.map(({ name, bg, hover }) => (
                    <button key={name} onClick={() => setSelectedSubject(name)}
                      className={`px-4 py-2 rounded-full font-semibold text-sm whitespace-nowrap transition-all ${
                        selectedSubject === name ? 'bg-teal-600 text-white shadow-lg shadow-teal-600/30' : `${bg} ${hover} text-slate-700`
                      }`}
                      data-testid={`ebook-subject-${name.toLowerCase()}`}>{name}</button>
                  ))}
                </div>
              </div>
            </div>
          </div>

          <div className="max-w-7xl mx-auto px-4 md:px-8 lg:px-12 py-6">
            <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
              <div className="flex items-center gap-3">
                <label className="flex items-center gap-2 cursor-pointer text-sm text-slate-600">
                  <Checkbox checked={showOnlyFree} onCheckedChange={setShowOnlyFree} />Free Only
                </label>
                <label className="flex items-center gap-2 cursor-pointer text-sm text-slate-600">
                  <Checkbox checked={showOnlyAudio} onCheckedChange={setShowOnlyAudio} />
                  <Headphones className="w-3 h-3" /> Audiobooks
                </label>
                {activeFiltersCount > 0 && <Button variant="ghost" size="sm" onClick={clearFilters} className="text-xs text-slate-500">Clear all</Button>}
              </div>
              <div className="flex items-center gap-3">
                <span className="text-sm text-slate-500">{filteredEbooks.length} results</span>
                <select value={sortBy} onChange={(e) => setSortBy(e.target.value)}
                  className="px-3 py-2 border border-slate-200 rounded-lg text-sm bg-white focus:ring-2 focus:ring-teal-500" data-testid="sort-select">
                  <option value="newest">Newest First</option>
                  <option value="popular">Most Popular</option>
                  <option value="rating">Highest Rated</option>
                  <option value="price-low">Price: Low to High</option>
                  <option value="price-high">Price: High to Low</option>
                </select>
              </div>
            </div>

            {activeFiltersCount > 0 && (
              <div className="flex flex-wrap gap-2 mb-4">
                {selectedSubject !== 'All' && <span className="inline-flex items-center gap-1 px-3 py-1 bg-teal-100 text-teal-800 rounded-full text-sm">{selectedSubject}<button onClick={() => setSelectedSubject('All')}><X className="w-3 h-3" /></button></span>}
                {selectedLevel && <span className="inline-flex items-center gap-1 px-3 py-1 bg-purple-100 text-purple-800 rounded-full text-sm">{selectedLevel}<button onClick={() => setSelectedLevel('')}><X className="w-3 h-3" /></button></span>}
                {showOnlyFree && <span className="inline-flex items-center gap-1 px-3 py-1 bg-green-100 text-green-800 rounded-full text-sm">Free Only<button onClick={() => setShowOnlyFree(false)}><X className="w-3 h-3" /></button></span>}
                {showOnlyAudio && <span className="inline-flex items-center gap-1 px-3 py-1 bg-violet-100 text-violet-800 rounded-full text-sm">Audiobooks<button onClick={() => setShowOnlyAudio(false)}><X className="w-3 h-3" /></button></span>}
              </div>
            )}

            {filteredEbooks.length > 0 ? (
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 gap-3 md:gap-6">
                {filteredEbooks.map(ebook => (
                  <Link key={ebook.id} to={`/ebooks/${ebook.id}`}
                    className="group bg-white rounded-xl overflow-hidden shadow-sm hover:shadow-xl transition-all duration-300 transform hover:-translate-y-1"
                    data-testid={`ebook-card-${ebook.id}`}>
                    <div className="relative aspect-[3/4] overflow-hidden">
                      <img src={ebook.cover_image} alt={ebook.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300" />
                      {(ebook.audio_enabled || ebook.has_audio) && (
                        <div
                          className="absolute top-2 right-2 bg-violet-600 text-white px-2 py-0.5 rounded-full shadow-lg flex items-center gap-1 text-[10px] font-bold"
                          title="Available as audiobook"
                          data-testid={`audiobook-badge-${ebook.id}`}
                        >
                          <Headphones className="w-3 h-3" /> Audiobook
                        </div>
                      )}
                      {ebook.price === 0 && <div className="absolute top-2 left-2 bg-green-500 px-2 py-0.5 rounded-full text-xs font-bold text-white shadow-lg">FREE</div>}
                      <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex items-end p-3">
                        <div className="text-white text-xs flex items-center gap-2">
                          {ebook.rating && <span className="flex items-center gap-1"><Star className="w-3 h-3 fill-yellow-400 text-yellow-400" />{ebook.rating}</span>}
                          {ebook.pages && <span>{ebook.pages} pages</span>}
                        </div>
                      </div>
                    </div>
                    <div className="p-3">
                      <h3 className="text-sm font-bold text-slate-800 mb-1 line-clamp-2 group-hover:text-teal-600 transition-colors">{ebook.title}</h3>
                      <p className="text-xs text-slate-500 mb-2 line-clamp-1">{ebook.author}</p>
                      <div className="flex items-center justify-between">
                        {isBought('ebook', ebook) ? <BoughtBadge testId={`ebook-bought-${ebook.id}`} /> : (
                          <span className={`text-sm font-bold ${ebook.price > 0 ? 'text-teal-600' : 'text-green-600'}`}>{ebook.price === 0 ? 'Free' : `TSh ${ebook.price?.toLocaleString()}`}</span>
                        )}
                        <div className="flex items-center gap-1 text-slate-400"><Download className="w-3 h-3" /><span className="text-xs">{ebook.downloads || 0}</span></div>
                      </div>
                    </div>
                  </Link>
                ))}
              </div>
            ) : (
              <div className="text-center py-16">
                <BookOpen className="w-16 h-16 text-slate-300 mx-auto mb-4" />
                <h3 className="text-xl font-bold text-slate-700 mb-2">No eBooks Found</h3>
                <p className="text-slate-500 mb-4">Try adjusting your search or filters</p>
                <Button onClick={clearFilters} variant="outline">Clear Filters</Button>
              </div>
            )}
          </div>
        </>
      )}

      {/* ============ PUBLIC LIBRARY ============ */}
      {activeSection === 'public-library' && (
        <>
          <div className="bg-white/95 backdrop-blur-md border-b border-slate-200 shadow-md sticky top-16 z-40" data-testid="ebook-filter-sticky-bar">
            <div className="max-w-7xl mx-auto px-4 md:px-8 lg:px-12 py-3">
              <div className="flex items-center gap-3">
                {/* Level Filter */}
                <div className="relative shrink-0">
                  <select value={libraryLevel} onChange={(e) => setLibraryLevel(e.target.value)}
                    className="appearance-none bg-indigo-600 text-white pl-3 pr-8 py-2 rounded-full font-semibold text-sm cursor-pointer shadow-lg shadow-indigo-600/20 focus:outline-none"
                    data-testid="library-level-filter">
                    <option value="" className="text-slate-900 bg-white">All Levels</option>
                    <option value="Primary" className="text-slate-900 bg-white">Primary</option>
                    <option value="Secondary" className="text-slate-900 bg-white">Secondary</option>
                  </select>
                  <ChevronRight className="absolute right-2.5 top-1/2 -translate-y-1/2 w-4 h-4 text-white pointer-events-none rotate-90" />
                </div>

                {/* Subject Pills */}
                <div className="flex gap-2 filter-pills-row flex-1 min-w-0">
                  {SUBJECT_STYLES.map(({ name, bg, hover }) => (
                    <button key={name} onClick={() => setLibrarySubject(name)}
                      className={`px-4 py-2 rounded-full font-semibold text-sm whitespace-nowrap transition-all ${
                        librarySubject === name ? 'bg-teal-600 text-white shadow-lg shadow-teal-600/30' : `${bg} ${hover} text-slate-700`
                      }`}
                      data-testid={`library-subject-${name.toLowerCase()}`}>{name}</button>
                  ))}
                </div>
              </div>
            </div>
          </div>

          <div className="max-w-7xl mx-auto px-4 md:px-8 lg:px-12 py-6">
            {/* Info Banner */}
            <div className="flex flex-wrap items-center gap-4 mb-6 p-4 bg-gradient-to-r from-amber-50 to-orange-50 border border-amber-200 rounded-xl" data-testid="library-info-banner">
              <Globe className="w-5 h-5 text-amber-600 shrink-0" />
              <p className="text-sm text-amber-800 flex-1">
                <strong>Free textbooks</strong> from OpenStax and other open educational resources. Read directly in the app — no redirects.
              </p>
              <div className="flex gap-2">
                {Object.entries(SOURCE_LABELS).map(([key, { name, color }]) => (
                  <span key={key} className={`${color} text-white px-2.5 py-1 rounded-full text-xs font-medium`}>{name}</span>
                ))}
              </div>
            </div>

            <div className="flex items-center justify-between mb-4">
              <span className="text-sm text-slate-500">{filteredLibrary.length} books available</span>
            </div>

            {libraryLoading ? (
              <div className="flex items-center justify-center py-20">
                <Loader2 className="w-8 h-8 text-teal-500 animate-spin" />
                <span className="ml-3 text-slate-500">Loading library...</span>
              </div>
            ) : filteredLibrary.length > 0 ? (
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 gap-3 md:gap-6">
                {filteredLibrary.map((book) => {
                  const internal = isInternalBook(book);
                  const CardWrapper = internal ? Link : 'a';
                  const cardProps = internal
                    ? { to: getLibraryBookLink(book) }
                    : { href: book.read_url, target: '_blank', rel: 'noopener noreferrer' };

                  return (
                    <CardWrapper
                      key={book.id}
                      {...cardProps}
                      className="group bg-white rounded-xl overflow-hidden shadow-sm hover:shadow-xl transition-all duration-300 transform hover:-translate-y-1"
                      data-testid={`library-book-${book.id}`}
                    >
                      <div className="relative aspect-[3/4] overflow-hidden">
                        {/* Subject-colored gradient cover */}
                        {(() => {
                          const cover = SUBJECT_COVERS[book.subject] || SUBJECT_COVERS.General;
                          return (
                            <div className="w-full h-full flex flex-col justify-between p-3"
                              style={{ background: `linear-gradient(135deg, ${cover.from} 0%, ${cover.to} 100%)` }}>
                              {/* Decorative circles */}
                              <div className="absolute top-0 right-0 w-24 h-24 rounded-full opacity-20"
                                style={{ background: cover.accent, transform: 'translate(30%, -30%)' }} />
                              <div className="absolute bottom-0 left-0 w-16 h-16 rounded-full opacity-15"
                                style={{ background: cover.accent, transform: 'translate(-20%, 20%)' }} />
                              {/* Large subject initial */}
                              <div className="text-white/15 text-7xl font-black leading-none self-end select-none">{cover.icon}</div>
                              {/* Title on cover */}
                              <div className="relative z-10">
                                <p className="text-white font-bold text-sm leading-tight line-clamp-3 drop-shadow-sm">{book.title}</p>
                                <p className="text-white/70 text-xs mt-1">{book.authors}</p>
                              </div>
                            </div>
                          );
                        })()}
                        {/* Badges */}
                        <div className={`absolute top-2 left-2 ${SOURCE_LABELS[book.source]?.color || 'bg-slate-500'} px-2 py-0.5 rounded-full text-[10px] font-bold text-white shadow-lg`}>
                          {SOURCE_LABELS[book.source]?.name || book.source}
                        </div>
                        <div className="absolute top-2 right-2 bg-green-500 px-2 py-0.5 rounded-full text-[10px] font-bold text-white shadow-lg">FREE</div>
                        {/* Hover overlay */}
                        <div className="absolute inset-0 bg-black/30 opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex items-center justify-center">
                          <span className="bg-white text-slate-800 px-4 py-2 rounded-full text-xs font-bold shadow-lg">{internal ? 'Read Now' : 'Open'}</span>
                        </div>
                      </div>
                      <div className="p-3">
                        <h3 className="text-sm font-bold text-slate-800 mb-1 line-clamp-2 group-hover:text-teal-600 transition-colors">{book.title}</h3>
                        <div className="flex items-center justify-between">
                          <span className="text-xs font-medium text-emerald-600">Free</span>
                          <span className="text-xs text-slate-400 bg-slate-100 px-1.5 py-0.5 rounded">{book.subject}</span>
                        </div>
                      </div>
                    </CardWrapper>
                  );
                })}
              </div>
            ) : (
              <div className="text-center py-16">
                <Library className="w-16 h-16 text-slate-300 mx-auto mb-4" />
                <h3 className="text-xl font-bold text-slate-700 mb-2">No Books Found</h3>
                <p className="text-slate-500 mb-4">Try different search terms or clear filters</p>
                <Button onClick={() => { setLibrarySearch(''); setLibrarySubject('All'); setLibraryLevel(''); loadLibraryBooks(); }} variant="outline">Browse All</Button>
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
}

export default AllEbooks;
